import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as d3 from 'd3';
import { AccountPortfolio } from '../../../types';

interface Props {
    portfolio: AccountPortfolio;
    width?: number;
    height?: number;
}

export const AssetDistributionChart: React.FC<Props> = ({
    portfolio,
    width = 400,
    height = 400
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const theme = useTheme();

    useEffect(() => {
        if (!portfolio?.asset_data?.distribution || !svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        // Transform portfolio data to chart data
        const distribution = portfolio.asset_data.distribution;
        const balances = portfolio.asset_data.balances || {};

        const data = Object.entries(distribution).map(([asset, share]) => {
            const symbol = balances[asset]?.symbol
                ? balances[asset].symbol.toUpperCase()
                : asset.split(":")[1] ?? asset;

            return {
                asset,
                symbol,
                value: Number(share),
                percentage: (Number(share) * 100).toFixed(1),
            };
        });

        // Chart dimensions
        const margin = { top: 60, right: 40, bottom: 40, left: 40 };
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;
        const radius = Math.min(chartWidth, chartHeight) / 2;

        // Root group centered
        // Adjust the root group to leave more space for title
        const g = svg
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${width / 2}, ${(height + 30) / 2})`); // Moved down slightly

        // Add title at the top
        svg.append('text')
            .attr('x', width / 2)
            .attr('y', 35)  // Changed from 25 to 20 to match the relative positioning
            .attr('text-anchor', 'middle')
            .attr('font-size', '14px')
            .attr('font-weight', 'bold')
            .attr('fill', theme.palette.text.primary)
            .text('Distribution');

        const color = d3.scaleOrdinal<string>()
            .range(d3.schemeTableau10.concat(d3.schemeSet3.flat()));

        // Pie generator
        const pie = d3
            .pie<typeof data[0]>()
            .padAngle(0.03)
            .sort(null)
            .value(d => d.value);

        // Arc generators
        const arc = d3
            .arc<d3.PieArcDatum<typeof data[0]>>()
            .innerRadius(0)
            .outerRadius(radius);

        // Label radius centered between center and edge; tweak as needed
        const arcLabel = d3
            .arc<d3.PieArcDatum<typeof data[0]>>()
            .innerRadius(radius * 0.72)
            .outerRadius(radius * 0.72);

        const arcs = pie(data);

        // Draw pie slices
        const slices = g
            .selectAll('.slice')
            .data(arcs)
            .enter()
            .append('g')
            .attr('class', 'slice');

        slices
            .append('path')
            .attr('d', arc)
            .attr('fill', (_d, i) => color(String(i)))
            .attr('stroke', theme.palette.background.paper)
            .attr('stroke-width', 1)
            .append('title')
            .text(d => `${d.data.symbol}\n${d.data.percentage}%`);

        // Labels on top of slices (asset bold, percentage normal)
        // For very thin slices, only show the asset line
        const labels = g
            .append('g')
            .attr('font-family', 'sans-serif')
            .attr('font-size', 12)
            .attr('text-anchor', 'middle')
            .selectAll('text')
            .data(arcs)
            .enter()
            .append('text')
            .attr('transform', d => `translate(${arcLabel.centroid(d)})`)
            .style('pointer-events', 'none');

        labels
            .selectAll('tspan')
            .data(d => {
                const lines = [d.data.symbol, `${d.data.percentage}%`];
                const angle = d.endAngle - d.startAngle;
                // Show both lines only if the wedge is wide enough
                return angle > 0.25 ? lines : lines.slice(0, 1);
            })
            .enter()
            .append('tspan')
            .attr('x', 0)
            .attr('y', (_line, i) => `${i * 1.1}em`)
            .attr('font-weight', (_line, i) => (i === 0 ? 'bold' : 'normal'))
            .attr('fill', theme.palette.getContrastText(theme.palette.background.paper))
            .text(line => line);

    }, [portfolio, width, height, theme]);

    if (!portfolio?.asset_data?.distribution) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height={height}>
                <Typography variant="body2" color="textSecondary">
                    No distribution data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <svg ref={svgRef} style={{ width: '100%', height: 'auto' }} />
        </Box>
    );
};

export default AssetDistributionChart;
